import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ABOUT_INFO } from '../../../utils/constants';
import { useWindows } from '../../../contexts/WindowContext';
import { Icon } from '../../Icons';
import styles from './MSNMessenger.module.css';

interface Contact {
  id: string;
  name: string;
  email: string;
  status: 'online' | 'away' | 'busy' | 'offline';
  avatar?: string;
  statusMessage?: string;
}

interface Message {
  id: string;
  sender: 'user' | 'contact';
  text: string;
  timestamp: Date;
}

const CONTACTS: Contact[] = [
  {
    id: 'adi',
    name: ABOUT_INFO.name,
    email: ABOUT_INFO.email,
    status: 'online',
    avatar: ABOUT_INFO.avatar,
    statusMessage: 'Available for new opportunities!',
  },
];

const AUTO_RESPONSES: string[] = [
  "Thanks for reaching out! I'll get back to you soon.",
  "That's a great question! Let me think about it...",
  "Interesting! Tell me more about that.",
  "I appreciate your message! Feel free to email me at " + ABOUT_INFO.email,
  "Thanks for stopping by my portfolio! 🙌",
  "I'm currently working on some exciting projects!",
  "Great to connect with you!",
];

const STATUS_COLORS: Record<string, string> = {
  online: '#7FBA00',
  away: '#FFB900',
  busy: '#E81123',
  offline: '#808080',
};

export const MSNMessenger: React.FC = () => {
  const { openWindow } = useWindows();
  const [view, setView] = useState<'contacts' | 'chat'>('contacts');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [userStatus, setUserStatus] = useState<'online' | 'away' | 'busy'>('online');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleContactClick = useCallback((contact: Contact) => {
    setSelectedContact(contact);
    setView('chat');
    // Add greeting message from contact
    if (messages.length === 0) {
      setMessages([{
        id: 'greeting',
        sender: 'contact',
        text: `Hey there! 👋 Thanks for checking out my portfolio. Feel free to send me a message!`,
        timestamp: new Date(),
      }]);
    }
  }, [messages.length]);

  const handleSendMessage = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || !selectedContact) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Simulate typing indicator
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const autoResponse: Message = {
        id: `contact-${Date.now()}`,
        sender: 'contact',
        text: AUTO_RESPONSES[Math.floor(Math.random() * AUTO_RESPONSES.length)],
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, autoResponse]);
    }, 1500 + Math.random() * 1500);
  }, [inputValue, selectedContact]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const renderContactsList = () => (
    <div className={styles.contactsList}>
      {/* User status header */}
      <div className={styles.userHeader}>
        <div className={styles.userInfo}>
          <div className={styles.userAvatar}>
            <span className={styles.defaultAvatar}>👤</span>
          </div>
          <div className={styles.userDetails}>
            <span className={styles.userName}>Guest</span>
            <select
              value={userStatus}
              onChange={(e) => setUserStatus(e.target.value as typeof userStatus)}
              className={styles.statusSelect}
            >
              <option value="online">Online</option>
              <option value="away">Away</option>
              <option value="busy">Busy</option>
            </select>
          </div>
        </div>
      </div>

      {/* Online contacts */}
      <div className={styles.contactsSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>▼</span>
          <span>Online ({CONTACTS.filter(c => c.status !== 'offline').length})</span>
        </div>
        {CONTACTS.filter(c => c.status !== 'offline').map(contact => (
          <button
            key={contact.id}
            className={styles.contactItem}
            onClick={() => handleContactClick(contact)}
          >
            <div className={styles.contactAvatar}>
              {contact.avatar ? (
                <img src={contact.avatar} alt={contact.name} />
              ) : (
                <span className={styles.defaultAvatar}>👤</span>
              )}
              <span
                className={styles.statusIndicator}
                style={{ backgroundColor: STATUS_COLORS[contact.status] }}
              />
            </div>
            <div className={styles.contactInfo}>
              <span className={styles.contactName}>{contact.name}</span>
              {contact.statusMessage && (
                <span className={styles.statusMessage}>{contact.statusMessage}</span>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Offline section */}
      <div className={styles.contactsSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>▶</span>
          <span>Offline (0)</span>
        </div>
      </div>

      {/* Footer actions */}
      <div className={styles.contactsFooter}>
        <a href={`mailto:${ABOUT_INFO.email}`} className={styles.footerLink}>
          📧 Send email to {ABOUT_INFO.name}
        </a>
      </div>
    </div>
  );

  const renderChat = () => (
    <div className={styles.chatView}>
      {/* Chat header */}
      <div className={styles.chatHeader}>
        <button className={styles.backButton} onClick={() => setView('contacts')}>
          ◀
        </button>
        <div className={styles.chatContactInfo}>
          <div className={styles.chatAvatar}>
            {selectedContact?.avatar ? (
              <img src={selectedContact.avatar} alt={selectedContact.name} />
            ) : (
              <span className={styles.defaultAvatar}>👤</span>
            )}
          </div>
          <div className={styles.chatContactDetails}>
            <span className={styles.chatContactName}>{selectedContact?.name}</span>
            <span className={styles.chatContactEmail}>{selectedContact?.email}</span>
          </div>
        </div>
      </div>

      {/* Messages area */}
      <div className={styles.messagesArea}>
        {messages.map(message => (
          <div
            key={message.id}
            className={`${styles.message} ${message.sender === 'user' ? styles.userMessage : styles.contactMessage}`}
          >
            <div className={styles.messageContent}>
              <span className={styles.messageSender}>
                {message.sender === 'user' ? 'You' : selectedContact?.name}
              </span>
              <span className={styles.messageTime}>{formatTime(message.timestamp)}</span>
              <p className={styles.messageText}>{message.text}</p>
            </div>
          </div>
        ))}
        {isTyping && (
          <div className={`${styles.message} ${styles.contactMessage}`}>
            <div className={styles.typingIndicator}>
              <span>{selectedContact?.name} is typing</span>
              <span className={styles.typingDots}>
                <span>.</span><span>.</span><span>.</span>
              </span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <form onSubmit={handleSendMessage} className={styles.inputArea}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Type a message..."
          className={styles.messageInput}
        />
        <button type="submit" className={styles.sendButton} disabled={!inputValue.trim()}>
          Send
        </button>
      </form>

      {/* Quick actions */}
      <div className={styles.quickActions}>
        <a href={`mailto:${ABOUT_INFO.email}`} className={styles.quickAction}>
          📧 Email
        </a>
        <span className={styles.quickAction} style={{ opacity: 0.5, cursor: 'not-allowed' }}>
          📞 Call
        </span>
        <span className={styles.quickAction} style={{ opacity: 0.5, cursor: 'not-allowed' }}>
          📹 Video
        </span>
      </div>
    </div>
  );

  return (
    <div className={styles.messenger}>
      {/* Title bar content */}
      <div className={styles.messengerHeader}>
        <Icon name="msn-messenger" size={20} />
        <span>MSN Messenger</span>
      </div>

      {/* Main content */}
      <div className={styles.content}>
        {view === 'contacts' ? renderContactsList() : renderChat()}
      </div>

      {/* Ad banner (for authenticity) */}
      <div
        className={styles.adBanner}
        onClick={() => openWindow('projects')}
        style={{ cursor: 'pointer' }}
      >
        <div className={styles.adContent}>
          <span>🚀 Check out my projects!</span>
          <span className={styles.adLink}>Open Projects folder →</span>
        </div>
      </div>
    </div>
  );
};

export default MSNMessenger;
